import { Tooltip, TooltipContent, TooltipTrigger } from '@leadsnavi/ui-kit';
import { ChartInput } from '../input';
import { SupplierSelectionBar } from '../base/supplier-selection-bar';
import styles from './styles.module.scss';
import cn from 'classnames';
import { useShallow } from 'zustand/react/shallow';
import { useChatPageStore } from '../../store';
import { useRef, useCallback, useState } from 'react';
import { useMemoizedFn, useMount } from 'ahooks';
import { autoClickedKey, AutoClickKeyValue } from '../supplier/use-supplier-model';
import { Storage } from '@/utils/storage';
import { SubContentType } from '../../types';

/**
 * 3 秒倒计时 hook
 * @param onFinish 倒计时结束时触发的回调函数
 * @returns { count, start } count: 当前倒计时数值，start: 启动倒计时的方法
 */
export const useCountdown = (onFinish: () => void) => {
  const TOTAL = 3;
  const [count, setCount] = useState<number>(TOTAL);
  const [isCounting, setIsCounting] = useState<boolean>(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    clear();
    setCount(TOTAL);
    setIsCounting(true);
    let current = TOTAL;
    timerRef.current = setInterval(() => {
      current -= 1;
      if (current <= 0) {
        setCount(0);
        setIsCounting(false);
        clear();
        onFinish();
      } else {
        setCount(current);
      }
    }, 1000);
  }, [clear, onFinish]);

  return { count, isCounting, start };
};

interface ChatInputBarProps {
  toggle: boolean;
  // A component props
  onSend?: (input: string) => Promise<void>;
  onAbort?: () => void;
  // B component props
  selectedCount: number;
  onSendInquiry: () => void;
  onClose: () => void;
  disabled?: boolean;
}

interface SupplierMountedActionProps {
  onSupplierBarVisible: () => void;
}

const SupplierMountedAction = (props: SupplierMountedActionProps) => {
  useMount(() => {
    props.onSupplierBarVisible();
  });
  return null;
};

export const ChatInputBarV2 = (props: ChatInputBarProps) => {
  // false input模式，true action模式
  const inputWrapRf = useRef<HTMLDivElement>(null);
  const { toggle, onSend, onAbort, selectedCount, onSendInquiry, onClose, disabled = false } = props;
  const asking = useChatPageStore(state => state.asking);
  const isResponding = useChatPageStore(state => state.isLoadingResponse);
  const showInputTooltip = useChatPageStore(state => state.showInputTooltip);
  const reportDetailLoadingMap = useChatPageStore(useShallow(state => state.reportDetailLoadingMap));
  const lastMsgSubMessages = useChatPageStore(
    useShallow(state => state.msgList[state.msgList.length - 1]?.subMessage ?? [])
  );
  const getStoreState = useChatPageStore(state => state.getStoreState);
  const setStoreState = useChatPageStore(state => state.setStoreState);

  // 最后一条消息含报告卡片 sub message，且所有报告详情均不在加载中 → isSending=true（按钮置灰转圈，等待报告就绪）
  const lastMsgReportCardIds = lastMsgSubMessages
    .filter(sub => sub.contentType === SubContentType.marketInsightReport)
    .map(sub => String((sub.contentValue as { reportId?: string } | null)?.reportId ?? ''))
    .filter(Boolean);
  const isWaitingForReport =
    lastMsgReportCardIds.length > 0 && lastMsgReportCardIds.some(id => reportDetailLoadingMap[id]);
  const { count, isCounting, start } = useCountdown(() => {
    Storage.setItem(autoClickedKey, { isOpend: true } as AutoClickKeyValue);
    setStoreState({
      isOpenAutoClick: false,
    });
    onSendInquiry();
  });
  console.log('asking', asking);
  console.log('isResponding', isResponding);
  const handleSupplierBarVisible = useMemoizedFn(() => {
    const isOpenAutoClick = getStoreState().isOpenAutoClick;
    if (isOpenAutoClick) {
      start();
    }
  });

  return (
    <div className="flex justify-center">
      <div className={cn(styles.container, toggle ? styles.containerActive : styles.inputActive)}>
        <Tooltip open={showInputTooltip}>
          <TooltipTrigger asChild>
            <div ref={inputWrapRf} className={cn(styles.inputContainer, !toggle ? styles.inputActive : '')}>
              <ChartInput
                isSending={asking || isWaitingForReport}
                isResponding={isResponding}
                onSend={onSend}
                onAbort={onAbort}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" align="end" className=" relative z-10">
            Ask me to change the report direction here
          </TooltipContent>
        </Tooltip>
        <div className={cn(styles.actionBarContainer, toggle ? styles.actionBarActive : '')}>
          {toggle ? <SupplierMountedAction onSupplierBarVisible={handleSupplierBarVisible} /> : null}
          <SupplierSelectionBar
            selectedCount={selectedCount}
            onSendInquiry={onSendInquiry}
            onClose={onClose}
            disabled={disabled}
            slot={isCounting ? <span className="text-red">({count}s)</span> : null}
          />
        </div>
      </div>
    </div>
  );
};
